import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Card,
  Header,
  Title,
  SubTitle,
  SearchContainer,
  InputWrapper,
  SearchInput,
  IconWrapper,
  ListContainer,
  ConsultantItem,
  Avatar,
  Info,
  Name,
  Role,
  LoginContainer,
  SelectedCard,
  BigAvatar,
  BackButton,
  Form,
  Label,
  Input,
  SubmitButton,
  FeedbackBox,
} from "./VincularConsultorStyle";

const VincularConsultor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Dados
  const [allConsultants, setAllConsultants] = useState([]);
  const [filteredConsultants, setFilteredConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState(null);

  // Inputs
  const [searchTerm, setSearchTerm] = useState("");
  const [loginInput, setLoginInput] = useState("");
  const [password, setPassword] = useState("");

  // Estados
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", text: "" });

  // 1. Carregar Lista
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_ROUTE}consultor/public-list`
        );
        if (response.ok) {
          const data = await response.json();
          // Ordena alfabeticamente só para garantir
          const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));

          setAllConsultants(sortedData);

          const urlSearch = searchParams.get("search");
          if (urlSearch) {
            setSearchTerm(urlSearch);
            setFilteredConsultants(
              sortedData.filter((c) =>
                c.name.toLowerCase().includes(urlSearch.toLowerCase())
              )
            );
          } else {
            setFilteredConsultants(sortedData);
          }
        }
      } catch (error) {
        console.error(error);
        setFeedback({
          type: "error",
          text: "Não foi possível carregar a lista.",
        });
      } finally {
        setIsLoadingList(false);
      }
    };
    fetchAll();
  }, [searchParams]);

  // 2. Filtragem
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (!term) {
      setFilteredConsultants(allConsultants);
      return;
    }

    const filtered = allConsultants.filter((c) =>
      c.name.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredConsultants(filtered);
  };

  const getInitials = (name) => {
    return name ? name.slice(0, 2).toUpperCase() : "GB";
  };

  // 3. Envio
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: "", text: "" });

    if (!loginInput || !password) {
      setFeedback({ type: "error", text: "Preencha todos os campos." });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        consultorId: selectedConsultant.id,
        consultorName: selectedConsultant.name,
        loginInput: loginInput,
        password: password,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_ROUTE}auth/link-consultant`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setFeedback({
          type: "success",
          text: "Vínculo realizado! Entrando...",
        });
        setTimeout(() => navigate("/"), 2000);
      } else {
        setFeedback({
          type: "error",
          text: data.message || "Credenciais inválidas.",
        });
      }
    } catch (err) {
      setFeedback({ type: "error", text: "Erro ao conectar com o servidor." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Card>
        <Header>
          <Title>Vincular Consultor</Title>
          <SubTitle>
            {!selectedConsultant
              ? "Encontre seu consultor Golden Brasil"
              : "Confirme sua identidade"}
          </SubTitle>
        </Header>

        {/* TELA 1: LISTA E BUSCA */}
        {!selectedConsultant && (
          <>
            <SearchContainer>
              <InputWrapper>
                <IconWrapper>
                  <i className="fa-solid fa-magnifying-glass" />
                </IconWrapper>
                <SearchInput
                  type="text"
                  placeholder="Pesquisar por nome..."
                  value={searchTerm}
                  onChange={handleSearch}
                  autoFocus
                />
              </InputWrapper>
            </SearchContainer>

            <ListContainer>
              {isLoadingList ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#9ca3af",
                  }}
                >
                  <i
                    className="fa-solid fa-spinner fa-spin"
                    style={{ marginRight: "8px" }}
                  ></i>
                  Carregando consultores...
                </div>
              ) : filteredConsultants.length > 0 ? (
                filteredConsultants.map((consultant) => (
                  <ConsultantItem
                    key={consultant.id}
                    onClick={() => setSelectedConsultant(consultant)}
                  >
                    <Avatar>{getInitials(consultant.name)}</Avatar>
                    <Info>
                      <Name>{consultant.name}</Name>
                      <Role>Consultor Autorizado</Role>
                    </Info>
                    <i
                      className="fa-solid fa-chevron-right"
                      style={{ color: "#d1d5db", fontSize: "14px" }}
                    ></i>
                  </ConsultantItem>
                ))
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    color: "#6b7280",
                  }}
                >
                  <i
                    className="fa-solid fa-user-slash"
                    style={{
                      fontSize: "30px",
                      marginBottom: "10px",
                      display: "block",
                      opacity: 0.5,
                    }}
                  ></i>
                  Nenhum consultor encontrado.
                </div>
              )}
            </ListContainer>
          </>
        )}

        {/* TELA 2: FORMULÁRIO */}
        {selectedConsultant && (
          <LoginContainer>
            <SelectedCard>
              <BigAvatar>{getInitials(selectedConsultant.name)}</BigAvatar>
              <h3 style={{ margin: "0 0 5px 0", color: "#111827" }}>
                {selectedConsultant.name}
              </h3>
              <span
                style={{
                  color: "#d4af37",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              >
                Consultor Selecionado
              </span>

              <BackButton
                type="button"
                onClick={() => setSelectedConsultant(null)}
              >
                <i
                  className="fa-solid fa-arrow-left"
                  style={{ marginRight: "5px" }}
                ></i>
                Trocar Consultor
              </BackButton>
            </SelectedCard>

            <Form onSubmit={handleSubmit}>
              <div>
                <Label>Usuário, Email ou CPF</Label>
                <Input
                  type="text"
                  value={loginInput}
                  onChange={(e) => setLoginInput(e.target.value)}
                  placeholder="Digite seu login"
                  required
                />
              </div>

              <div>
                <Label>Senha</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha de acesso"
                  required
                />
              </div>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i
                      className="fa-solid fa-circle-notch fa-spin"
                      style={{ marginRight: "8px" }}
                    ></i>{" "}
                    Processando...
                  </>
                ) : (
                  "Confirmar Vínculo"
                )}
              </SubmitButton>
            </Form>

            {feedback.text && (
              <FeedbackBox type={feedback.type}>{feedback.text}</FeedbackBox>
            )}
          </LoginContainer>
        )}
      </Card>
    </Container>
  );
};

export default VincularConsultor;
